import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Button } from 'antd';
import { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import { getEmptyRuleGroupInAPIForm } from '@src/shared/utils/eventBuilder';
import { useGetEventNames } from '@src/services/query/applang/queryHooks';
import RuleItem from './RuleItem';
import {
  RawEvent,
  RawEventTitle,
  OrTag,
  DeleteIcon,
  RawEventContent,
  AndRuleContent,
  AndRuleLine,
  LineLeft,
  AndText,
  LineRight,
  RawEventFooter,
  AutoComplete,
} from './styled';
import DeleteSvg from '@src/icons/trash.svg';
import { useCriticalEventInterval } from '@src/features/CriticalEventFormContainer';

interface IProps {
  event: CriticalEventRule;
  onDelete?: (event: CriticalEventRule) => void;
  onClick?: (event: CriticalEventRule) => void;
  onChange?: (event: CriticalEventRule) => void;
  index: number;
  isActive?: boolean;
  isHighlight?: boolean;
  errorFields?: string[];
  errorIndexs?: number[];
}
const getEmptyRuleItem = () => {
  return getEmptyRuleGroupInAPIForm().mapping_rules[0][0];
};

const EventBox: React.FC<IProps> = ({
  event,
  index,
  isHighlight,
  onDelete,
  onChange,
  onClick,
  errorFields,
  errorIndexs,
}) => {
  // State for search input (immediate update for UI)
  const [searchInput, setSearchInput] = useState<string>('');
  // Debounced search key for API calls
  const [debouncedSearchKey, setDebouncedSearchKey] = useState<string>('');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const interval = useCriticalEventInterval();
  // Debounce search input
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchKey(searchInput);
    }, 300); // 300ms debounce

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchInput]);

  // Get time range from global store
  // Fetch event names from API with debounced search key
  const { data: eventNames, isLoading: isGetEventNameLoading } = useGetEventNames({
    time_range: interval,
    key: debouncedSearchKey, // Use debounced search key for filtering
  });

  // Transform event names to options format
  const rawEventNameOptions = useMemo(() => {
    if (!eventNames || eventNames.length === 0) return [];
    return eventNames.map((name) => ({
      label: name,
      value: name,
    }));
  }, [eventNames]);

  const loadingSuffix = useMemo(() => {
    if (isGetEventNameLoading) return <LoadingOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />;
    return <DownOutlined />;
  }, [isGetEventNameLoading]);

  const addRuleItem = (type: 'OR' | 'AND') => {
    const newEvent = {
      ...event,
      mapping_rules: [...event.mapping_rules.map((group) => [...group])],
    };
    if (type === 'OR') {
      const lastOrGroup = newEvent.mapping_rules[newEvent.mapping_rules.length - 1];
      if (lastOrGroup) {
        lastOrGroup.push(getEmptyRuleItem());
      } else {
        newEvent.mapping_rules.push([getEmptyRuleItem()]);
      }
    } else {
      newEvent.mapping_rules.push([getEmptyRuleItem()]);
    }
    onChange?.(newEvent);
  };

  const deleteAndLine = (_ruleAndIndex: number) => {
    const newEvent = {
      ...event,
      mapping_rules: [...event.mapping_rules.map((group) => [...group])],
    };
    const andGroup = newEvent.mapping_rules?.[_ruleAndIndex];
    if (andGroup?.length > 0) {
      newEvent.mapping_rules?.splice(_ruleAndIndex, 1);
    }
    onChange?.(newEvent);
  };

  const deleteRuleItem = (ruleAndIndex, ruleOrIndex) => {
    event.mapping_rules[ruleAndIndex].splice(ruleOrIndex, 1);

    if (event.mapping_rules[ruleAndIndex].length === 0) {
      // Delete last orRule in andGroup need to delete this andRule as well
      event.mapping_rules.splice(ruleAndIndex, 1);
    }

    if (event.mapping_rules.length === 0) {
      // Delete last rule, clear rule input box.
      const curRule = getEmptyRuleItem();
      event.mapping_rules[0] = [curRule];
    }
    onChange?.(event);
  };

  const renderEventRules = (
    rawEventName: string[],
    rules: CriticalEventMappingRule[],
    eventIndex: number,
    ruleAndIndex: number,
  ) => {
    return (
      <div key={`key_${eventIndex}_${ruleAndIndex}`}>
        <AndRuleContent>
          {ruleAndIndex !== 0 && (
            <AndRuleLine>
              <LineLeft />
              <AndText>AND</AndText>
              <LineRight />
              <DeleteIcon>
                <Button
                  type="text"
                  icon={<DeleteSvg />}
                  onClick={() => deleteAndLine(ruleAndIndex)}
                />
              </DeleteIcon>
            </AndRuleLine>
          )}
          {rules.map((rule, ruleOrIndex) => (
            <RuleItem
              errorFields={errorFields || []}
              rawEventName={rawEventName?.[0] || ''}
              showHeader={ruleOrIndex !== 0}
              key={`key_${eventIndex}_${ruleAndIndex}_${ruleOrIndex}`}
              rule={rule}
              onChange={(newRule) => {
                rule = newRule;
                onChange?.(event);
              }}
              onDelete={() => {
                deleteRuleItem(ruleAndIndex, ruleOrIndex);
              }}
            />
          ))}
        </AndRuleContent>
      </div>
    );
  };

  const rawEventName = useMemo(() => {
    return event.value?.[0];
  }, [event.value]);

  return (
    <RawEvent isHighlight={isHighlight} onClick={() => onClick?.(event)}>
      <RawEventTitle>
        {index !== 0 && <OrTag>OR</OrTag>}
        <span style={{ display: 'flex', alignItems: 'center', marginRight: 0, flex: 1 }}>
          <span>Raw Event = </span>
          <AutoComplete
            onClick={(e) => {
              e.stopPropagation();
            }}
            style={{ marginLeft: 4, flex: 1 }}
            data-test-id={`raw_event_name_${index + 1}`}
            suffixIcon={loadingSuffix}
            disabled={false}
            value={rawEventName}
            status={errorFields?.includes('raw_event_name') && errorIndexs ? 'error' : undefined}
            options={rawEventNameOptions}
            placeholder="Type or select..."
            filterOption={false} // Disable client-side filtering, use server-side
            onSearch={(value) => {
              // Update search input (will be debounced for API calls)
              setSearchInput(value || '');
            }}
            onChange={(value) => {
              if (value === event.value?.[0]) return; // Avoid unnecessary updates
              const newEvent = {
                ...event,
                value: value ? [value as string] : [],
              };
              onChange?.(newEvent as CriticalEventRule);
            }}
          />
        </span>
        <DeleteIcon>
          <Button
            type="text"
            icon={<DeleteSvg />}
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(event);
            }}
          />
        </DeleteIcon>
      </RawEventTitle>

      <RawEventContent>
        {event.mapping_rules?.map((rules, ruleAndIndex) => {
          return renderEventRules(event.value, rules, index, ruleAndIndex);
        })}
      </RawEventContent>

      <RawEventFooter>
        <span onClick={() => addRuleItem('OR')}>Add OR Rule</span>
        <span onClick={() => addRuleItem('AND')}>Add AND Rule</span>
      </RawEventFooter>
    </RawEvent>
  );
};

export default EventBox;
